/* Header navigation controller.
   Owns: desktop mega-panel open/close (hover + keyboard, close on mouse leave),
   mobile drill-down menu (hamburger, forward/back panels), and the mobile search
   toggle. One instance per header section. */
(() => {
  window.DigitalizModules = window.DigitalizModules || {};
  if (window.DigitalizModules.HeaderNav) return;

  const CLOSE_DELAY = 120; // ms grace period before a hovered panel closes

  class HeaderNav {
    constructor(el) {
      this.el = el;
      this.bar = el.querySelector(".site-header__bar");
      this.mobileMenu = el.querySelector("[data-mobile-menu]");
      this.hamburger = el.querySelector("[data-mobile-menu-toggle]");
      this.searchToggle = el.querySelector("[data-search-toggle]");
      this.mobileSearch = el.querySelector("[data-mobile-search]");

      this.openItem = null;
      this.closeTimer = null;
      this.suppressFocusOpen = false;
      this.panelStack = []; // stack of open mobile sub-panels

      this.onDocKeydown = this.onDocKeydown.bind(this);
      this.onDocClickForSearch = this.onDocClickForSearch.bind(this);

      this.init();
    }

    init() {
      this.measureBar();
      window.addEventListener("resize", () => {
        this.measureBar();
        // Above the compact-search breakpoint the overlay is gone — reset its state
        if (window.innerWidth >= 1200) this.closeMobileSearch();
      }, { passive: true });

      this.bindDesktop();
      this.bindMobileMenu();
      this.bindMobileSearch();
    }

    measureBar() {
      const rect = this.el.getBoundingClientRect();
      // Full header height (desktop panel max-height) + its bottom edge in the
      // viewport (where the mobile menu starts, below any announcement bar).
      if (rect.height) this.el.style.setProperty("--header-bar-h", `${rect.height}px`);
      this.el.style.setProperty("--header-bottom", `${Math.max(0, rect.bottom)}px`);
    }

    /* ── Desktop mega panels ─────────────────────────────────────────────── */

    bindDesktop() {
      this.el.querySelectorAll("[data-nav-item].has-children").forEach((item) => {
        item.addEventListener("mouseenter", () => {
          this.clearCloseTimer();
          this.openPanel(item);
        });
        item.addEventListener("mouseleave", () => this.scheduleClose());

        // Keyboard: opening on focus, closing when focus leaves the item subtree
        item.addEventListener("focusin", () => {
          if (this.suppressFocusOpen) return;
          this.clearCloseTimer();
          this.openPanel(item);
        });
        item.addEventListener("focusout", (event) => {
          if (!item.contains(event.relatedTarget)) this.closePanel(item);
        });

        // Close button inside the panel
        const closeBtn = item.querySelector("[data-nav-close]");
        if (closeBtn) closeBtn.addEventListener("click", () => this.closeFromButton(item));

        // Level-2 → level-3 activation (hover + keyboard); JS-driven so the
        // level-3 heading can be aligned to the active level-2 item.
        item.querySelectorAll("[data-l2-item].has-children").forEach((l2) => {
          l2.addEventListener("mouseenter", () => this.activateL2(item, l2));
          l2.addEventListener("focusin", () => this.activateL2(item, l2));
        });
      });
    }

    openPanel(item) {
      if (this.openItem && this.openItem !== item) this.closePanel(this.openItem);
      this.openItem = item;
      item.classList.add("is-open");
      const trigger = item.querySelector("[data-nav-trigger]");
      if (trigger) trigger.setAttribute("aria-expanded", "true");
    }

    closePanel(item) {
      item.classList.remove("is-open");
      const trigger = item.querySelector("[data-nav-trigger]");
      if (trigger) trigger.setAttribute("aria-expanded", "false");
      item.querySelectorAll("[data-l2-item].is-open").forEach((l2) => {
        l2.classList.remove("is-open");
        const l2Trigger = l2.querySelector("[data-l2-trigger]");
        if (l2Trigger) l2Trigger.setAttribute("aria-expanded", "false");
      });
      if (this.openItem === item) this.openItem = null;
    }

    // Close via the panel's X: hand focus back to the level-1 trigger without
    // that focus re-opening the panel. Hover can't re-open it either until the
    // pointer leaves the item and comes back (the panel under it is gone).
    closeFromButton(item) {
      this.clearCloseTimer();
      this.closePanel(item);
      const trigger = item.querySelector("[data-nav-trigger]");
      if (!trigger) return;
      this.suppressFocusOpen = true;
      trigger.focus();
      this.suppressFocusOpen = false;
    }

    activateL2(item, l2) {
      item.querySelectorAll("[data-l2-item].is-open").forEach((other) => {
        if (other !== l2) {
          other.classList.remove("is-open");
          const t = other.querySelector("[data-l2-trigger]");
          if (t) t.setAttribute("aria-expanded", "false");
        }
      });
      l2.classList.add("is-open");
      const trigger = l2.querySelector("[data-l2-trigger]");
      if (trigger) trigger.setAttribute("aria-expanded", "true");
      this.alignL3(l2);
    }

    // Align the level-3 column so its heading lines up with the active level-2
    // item; the heading's underline then runs continuously beneath both. The
    // heading also takes the level-2 link's height (--header-l2-active-h) so a
    // level-2 title that wraps to two lines keeps the underline below both lines.
    alignL3(l2) {
      const l3 = l2.querySelector("[data-l3]");
      const grid = l2.closest(".header-nav__panel-grid");
      if (!l3 || !grid) return;
      const top = l2.getBoundingClientRect().top - grid.getBoundingClientRect().top;
      l3.style.top = `${Math.max(0, top)}px`;
      const link = l2.querySelector(".header-nav__l2-link");
      if (link) l3.style.setProperty("--header-l2-active-h", `${link.offsetHeight}px`);
    }

    scheduleClose() {
      this.clearCloseTimer();
      this.closeTimer = window.setTimeout(() => {
        if (this.openItem) this.closePanel(this.openItem);
      }, CLOSE_DELAY);
    }

    clearCloseTimer() {
      if (this.closeTimer) {
        window.clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
    }

    /* ── Mobile drill-down menu ──────────────────────────────────────────── */

    bindMobileMenu() {
      if (!this.mobileMenu || !this.hamburger) return;

      this.hamburger.addEventListener("click", () => {
        if (this.mobileMenu.classList.contains("is-open")) {
          this.closeMenu();
        } else {
          this.openMenu();
        }
      });

      this.mobileMenu.querySelectorAll("[data-mm-open]").forEach((btn) => {
        btn.addEventListener("click", () => this.openMMPanel(btn.getAttribute("data-mm-open"), btn));
      });

      this.mobileMenu.querySelectorAll("[data-mm-back]").forEach((btn) => {
        btn.addEventListener("click", () => this.mmBack());
      });
    }

    openMenu() {
      this.measureBar();
      this.mobileMenu.classList.add("is-open");
      this.mobileMenu.setAttribute("aria-hidden", "false");
      this.hamburger.classList.add("is-active");
      this.hamburger.setAttribute("aria-expanded", "true");
      document.body.classList.add("mobile-menu-open");
      document.addEventListener("keydown", this.onDocKeydown);
      this.closeMobileSearch();
    }

    closeMenu(restoreFocus = true) {
      this.mobileMenu.classList.remove("is-open");
      this.mobileMenu.setAttribute("aria-hidden", "true");
      this.hamburger.classList.remove("is-active");
      this.hamburger.setAttribute("aria-expanded", "false");
      document.body.classList.remove("mobile-menu-open");
      document.removeEventListener("keydown", this.onDocKeydown);
      this.resetPanels();
      if (restoreFocus) this.hamburger.focus();
    }

    openMMPanel(id, trigger) {
      const panel = id ? this.mobileMenu.querySelector(`#${CSS.escape(id)}`) : null;
      if (!panel) return;
      panel.classList.add("is-open");
      this.panelStack.push(panel);
      if (trigger) trigger.setAttribute("aria-expanded", "true");
      const focusTarget = panel.querySelector("[data-mm-back]") || panel;
      window.setTimeout(() => focusTarget.focus(), 50);
    }

    mmBack() {
      const panel = this.panelStack.pop();
      if (!panel) return;
      panel.classList.remove("is-open");
      const opener = this.mobileMenu.querySelector(`[data-mm-open="${panel.id}"]`);
      if (opener) {
        opener.setAttribute("aria-expanded", "false");
        opener.focus();
      }
    }

    resetPanels() {
      this.panelStack.forEach((panel) => panel.classList.remove("is-open"));
      this.panelStack = [];
      this.mobileMenu.querySelectorAll("[data-mm-open]").forEach((btn) => {
        btn.setAttribute("aria-expanded", "false");
      });
    }

    onDocKeydown(event) {
      if (event.key !== "Escape") return;
      if (this.panelStack.length) {
        this.mmBack();
      } else {
        this.closeMenu();
      }
    }

    /* ── Mobile search overlay ───────────────────────────────────────────── */

    bindMobileSearch() {
      if (!this.searchToggle || !this.mobileSearch) return;
      this.searchToggle.addEventListener("click", () => {
        if (this.mobileSearch.hasAttribute("hidden")) {
          this.openMobileSearch();
        } else {
          this.closeMobileSearch();
        }
      });
    }

    openMobileSearch() {
      this.mobileSearch.removeAttribute("hidden");
      this.searchToggle.setAttribute("aria-expanded", "true");
      const input = this.mobileSearch.querySelector("input[type='search']");
      if (input) input.focus();
      document.addEventListener("click", this.onDocClickForSearch);
    }

    closeMobileSearch() {
      if (!this.mobileSearch) return;
      this.mobileSearch.setAttribute("hidden", "");
      if (this.searchToggle) this.searchToggle.setAttribute("aria-expanded", "false");
      document.removeEventListener("click", this.onDocClickForSearch);
    }

    // Close the overlay when clicking anywhere outside it and outside the toggle.
    onDocClickForSearch(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (this.mobileSearch.contains(target)) return;
      if (this.searchToggle && this.searchToggle.contains(target)) return;
      this.closeMobileSearch();
    }
  }

  window.DigitalizModules.HeaderNav = HeaderNav;

  function init() {
    document.querySelectorAll("[data-header]").forEach((el) => {
      if (el.dataset.headerBound === "true") return;
      el.dataset.headerBound = "true";
      new HeaderNav(el);
    });
  }

  document.addEventListener("DOMContentLoaded", init);
  document.addEventListener("shopify:section:load", init);
})();
